import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Badge,
  Container,
  DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
} from "reactstrap";
import { MoreVertical, PlusCircle } from 'react-feather';
import { FaPen, FaTrashAlt, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { baseURL } from "../../../../Services/api/baseURL";
import CommonModal from "../../../UiKits/Modals/common/modal";
import useRbac from "../../../../Hooks/useRbac";

const apiBase = `${baseURL}/api`;

const authHeader = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  return { Authorization: token };
};

const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  alternative_phone_number: "",
  password: "",
  role_id: "",
  status: "active",
};

/**
 * Default password convention: `<CapitalizedFirstName>@123`.
 * e.g. "deepak" -> "Deepak@123". Admin can override before submitting.
 * Returns "" when first name is empty so the placeholder stays visible.
 */
const defaultPasswordFor = (firstName) => {
  const trimmed = (firstName || "").trim();
  if (!trimmed) return "";
  const cleaned = trimmed.replace(/\s+/g, "");
  const cap = cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
  return `${cap}@123`;
};

const createSchema = Yup.object({
  first_name: Yup.string().trim().required("First name is required"),
  last_name: Yup.string().trim().nullable(),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string().trim().min(6, "Phone is too short").required("Phone is required"),
  alternative_phone_number: Yup.string().trim().nullable(),
  password: Yup.string().min(8, "Min 8 characters").required("Password is required"),
  role_id: Yup.string().required("Role is required"),
  status: Yup.string().oneOf(["active", "inactive"]),
});

const updateSchema = Yup.object({
  first_name: Yup.string().trim().required("First name is required"),
  last_name: Yup.string().trim().nullable(),
  phone: Yup.string().trim().min(6).required("Phone is required"),
  alternative_phone_number: Yup.string().trim().nullable(),
  password: Yup.string().notRequired().min(8, "Min 8 characters"),
  status: Yup.string().oneOf(["active", "inactive"]),
});

const EmployeesTable = () => {
  const rbac = useRbac();
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [editTarget, setEditTarget] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Tracks whether the create-form password was hand-edited; while false, we
  // keep it in sync with the first name as `<FirstName>@123`.
  const [passwordTouched, setPasswordTouched] = useState(false);

  const isEdit = !!editTarget;

  const formik = useFormik({
    initialValues,
    validationSchema: isEdit ? updateSchema : createSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (isEdit) {
          const { password, status, role_id, email: _email, ...rest } = values;
          const patch = { ...rest };
          if (password) patch.password = password;
          if (status) patch.status = status;
          await axios.patch(`${apiBase}/employees/${editTarget._id}`, patch, {
            headers: authHeader(),
          });

          // Role change is a separate endpoint.
          if (role_id && role_id !== editTarget.role_id?._id) {
            await axios.patch(
              `${apiBase}/employees/${editTarget._id}/role`,
              { role_id },
              { headers: authHeader() }
            );
          }
          Swal.fire({ icon: "success", title: "Employee updated", timer: 1500, showConfirmButton: false });
        } else {
          await axios.post(`${apiBase}/employees`, values, { headers: authHeader() });
          Swal.fire({ icon: "success", title: "Employee created", timer: 1500, showConfirmButton: false });
        }
        closeModal();
        load();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not save",
          text: err?.response?.data?.message || err.message,
        });
      }
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: perPage };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (roleFilter) params.role_id = roleFilter;
      const res = await axios.get(`${apiBase}/employees`, { headers: authHeader(), params });
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed to load employees", text: err?.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, statusFilter, roleFilter]);

  const loadRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/roles`, { headers: authHeader() });
      setRoles(res.data?.data || []);
    } catch (err) {
      // Role list failure is non-fatal here; the form just won't have options.
      console.warn("Failed to load roles", err?.response?.data || err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRoles(); }, [loadRoles]);

  const openCreate = () => {
    setEditTarget(null);
    setPasswordTouched(false);
    formik.resetForm({ values: initialValues });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditTarget(row);
    setPasswordTouched(true); // edit mode: never auto-fill from first name
    formik.resetForm({
      values: {
        first_name: row.first_name || "",
        last_name: row.last_name || "",
        email: row.email || "",
        phone: row.phone || "",
        alternative_phone_number: row.alternative_phone_number || "",
        password: "",
        role_id: row.role_id?._id || "",
        status: row.status || "active",
      },
    });
    setModalOpen(true);
  };

  // Keep the create-form password in sync with the first name until the admin
  // edits it themselves. e.g. first_name "Deepak" -> password "Deepak@123".
  const onFirstNameChange = (e) => {
    formik.handleChange(e);
    if (!isEdit && !passwordTouched) {
      formik.setFieldValue("password", defaultPasswordFor(e.target.value));
    }
  };

  const onPasswordChange = (e) => {
    if (!isEdit) setPasswordTouched(true);
    formik.handleChange(e);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
    formik.resetForm({ values: initialValues });
  };

  const onDelete = async (row) => {
    if (row.is_seed) {
      Swal.fire({ icon: "error", title: "Cannot delete", text: "The seeded super admin cannot be deleted." });
      return;
    }
    if (String(row._id) === String(rbac.currentUserId)) {
      Swal.fire({ icon: "error", title: "Cannot delete", text: "You cannot delete your own account." });
      return;
    }
    const ok = await Swal.fire({
      icon: "warning",
      title: `Delete ${row.first_name} ${row.last_name || ""}?`,
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${apiBase}/employees/${row._id}`, { headers: authHeader() });
      Swal.fire({ icon: "success", title: "Deleted", timer: 1200, showConfirmButton: false });
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Delete failed", text: err?.response?.data?.message || err.message });
    }
  };

  const onToggleStatus = async (row) => {
    if (row.is_seed) {
      Swal.fire({ icon: "error", title: "Cannot change status", text: "The seeded super admin cannot be modified." });
      return;
    }
    const next = row.status === "active" ? "inactive" : "active";
    try {
      await axios.patch(
        `${apiBase}/employees/${row._id}/status`,
        { status: next },
        { headers: authHeader() }
      );
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Status change failed", text: err?.response?.data?.message || err.message });
    }
  };

  const columns = useMemo(() => [
    {
      name: "Name",
      selector: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim(),
      sortable: true,
      cell: (r) => (
        <span>
          {r.first_name} {r.last_name || ""}
          {r.is_seed && <Badge color="warning" className="ms-2">SEED</Badge>}
          {r.is_super_admin && <Badge color="dark" className="ms-1">SUPER</Badge>}
        </span>
      ),
    },
    { name: "Email", selector: (r) => r.email, sortable: true, grow: 2 },
    { name: "Phone", selector: (r) => r.phone },
    {
      name: "Role",
      selector: (r) => r.role_id?.role_name || "—",
      cell: (r) => (
        <span>
          {r.role_id?.role_name || "—"}
          {r.role_id?.code && <small className="text-muted ms-1">({r.role_id.code})</small>}
        </span>
      ),
    },
    {
      name: "Status",
      cell: (r) => (
        <Badge color={r.status === "active" ? "success" : "secondary"}>{r.status}</Badge>
      ),
      width: "110px",
    },
    {
      name: "Created",
      selector: (r) => r.createdAt,
      format: (r) => moment(r.createdAt).format("DD MMM YYYY"),
      width: "130px",
    },
    {
      name: "Actions",
      width: "120px",
      sortable: false,
      center: true,
      omit: !rbac.canAny([
        "employees.update",
        "employees.change_status",
        "employees.delete",
      ]),

      cell: (r) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn" color="link">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>

          <DropdownMenu>
            {rbac.can("employees.update") && (
              <DropdownItem onClick={() => openEdit(r)}>
                Edit
                <FaPen className="ms-1" />
              </DropdownItem>
            )}

            {rbac.can("employees.change_status") && !r.is_seed && (
              <DropdownItem onClick={() => onToggleStatus(r)}>
                {r.status === "active" ? "Deactivate" : "Activate"}

                {r.status === "active" ? (
                  <FaToggleOn className="ms-1" />
                ) : (
                  <FaToggleOff className="ms-1" />
                )}
              </DropdownItem>
            )}

            {rbac.can("employees.delete") && !r.is_seed && (
              <DropdownItem onClick={() => onDelete(r)}>
                Delete
                <FaTrashAlt className="ms-1 text-danger" />
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    }
    // {
    //   name: "Actions",
    //   width: "180px",
    //   omit: !rbac.canAny(['employees.update', 'employees.change_status', 'employees.delete']),
    //   cell: (r) => (
    //     <div className="d-flex gap-2 align-items-center">
    //       {rbac.can("employees.update") && (
    //         <Button size="sm" color="link" title="Edit" onClick={() => openEdit(r)}>
    //           <FaPen />
    //         </Button>
    //       )}
    //       {rbac.can("employees.change_status") && !r.is_seed && (
    //         <Button
    //           size="sm"
    //           color="link"
    //           title={r.status === "active" ? "Deactivate" : "Activate"}
    //           onClick={() => onToggleStatus(r)}
    //         >
    //           {r.status === "active" ? <FaToggleOn /> : <FaToggleOff />}
    //         </Button>
    //       )}
    //       {rbac.can("employees.delete") && !r.is_seed && (
    //         <Button size="sm" color="link" title="Delete" onClick={() => onDelete(r)}>
    //           <FaTrashAlt color="red" />
    //         </Button>
    //       )}
    //     </div>
    //   ),
    // },
  ], [rbac, openEdit, onDelete, onToggleStatus]);

  return (
    <Fragment>
      <Row className="mb-3 align-items-center">
        <Col md="3">
          {/* <h4 className="mb-0">
            <FaUserShield className="me-2" />
            Employees
          </h4> */}
          <h5 className="fw-bolder">Employees</h5>
        </Col>

        <Col md="3">
          <Input
            placeholder="Search by name, email, phone…"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          />
        </Col>
        <Col md="2">
          <Input
            type="select"
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Input>
        </Col>
        <Col md="2">
          <Input
            type="select"
            value={roleFilter}
            onChange={(e) => { setPage(1); setRoleFilter(e.target.value); }}
          >
            <option value="">All roles</option>
            {roles.map((r) => (
              <option key={r._id} value={r._id}>{r.role_name}</option>
            ))}
          </Input>
        </Col>
        <Col md="2" className="text-end">
          {rbac.can("employees.create") && (
            <Button className='btn btn-primary d-flex align-items-center ms-3' onClick={openCreate}>
              <PlusCircle size={16} className="me-1" /> Add Employee
            </Button>
          )}
        </Col>
      </Row>

      <DataTable
        columns={columns}
        data={rows}
        progressPending={loading}
        pagination
        paginationServer
        paginationDefaultPage={page}
        paginationPerPage={perPage}
        paginationTotalRows={total}
        onChangePage={(p) => setPage(p)}
        onChangeRowsPerPage={(pp, p) => { setPerPage(pp); setPage(p); }}
        highlightOnHover
        persistTableHead
      />

      <CommonModal
        isOpen={modalOpen}
        title={isEdit ? "Edit Employee" : "Add Employee"}
        toggler={closeModal}
        size="lg"
        className="store_modal"
      >
        <Container>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>First name *</Label>
                  <Input
                    name="first_name"
                    value={formik.values.first_name}
                    onChange={onFirstNameChange}
                    invalid={!!(formik.touched.first_name && formik.errors.first_name)}
                  />
                  {formik.touched.first_name && formik.errors.first_name && (
                    <small className="text-danger">{formik.errors.first_name}</small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Last name</Label>
                  <Input
                    name="last_name"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    disabled={isEdit}
                    invalid={!!(formik.touched.email && formik.errors.email)}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <small className="text-danger">{formik.errors.email}</small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Phone *</Label>
                  <Input
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    invalid={!!(formik.touched.phone && formik.errors.phone)}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <small className="text-danger">{formik.errors.phone}</small>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Alternative phone</Label>
                  <Input
                    name="alternative_phone_number"
                    value={formik.values.alternative_phone_number}
                    onChange={formik.handleChange}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>{isEdit ? "New password (leave blank to keep current)" : "Password *"}</Label>
                  <Input
                    type="text"
                    name="password"
                    value={formik.values.password}
                    onChange={onPasswordChange}
                    placeholder={isEdit ? "" : "Auto-filled as FirstName@123"}
                    invalid={!!(formik.touched.password && formik.errors.password)}
                  />
                  {!isEdit && !passwordTouched && (
                    <small className="text-muted d-block mt-1">
                      Defaults to <code>{defaultPasswordFor(formik.values.first_name) || "FirstName@123"}</code> — type to override.
                    </small>
                  )}
                  {formik.touched.password && formik.errors.password && (
                    <small className="text-danger">{formik.errors.password}</small>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Role *</Label>
                  <Input
                    type="select"
                    name="role_id"
                    value={formik.values.role_id}
                    onChange={formik.handleChange}
                    invalid={!!(formik.touched.role_id && formik.errors.role_id)}
                  >
                    <option value="">Select a role…</option>
                    {roles.map((r) => (
                      <option key={r._id} value={r._id}>
                        {r.role_name}
                        {r.code ? ` (${r.code})` : ""}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.role_id && formik.errors.role_id && (
                    <small className="text-danger">{formik.errors.role_id}</small>
                  )}
                  {!rbac.isSuperAdmin && (
                    <small className="text-muted d-block mt-1">
                      Only a super admin can assign the Super Admin role.
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    name="status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <div className="text-end mt-3">
              <Button type="button" className="me-2 cancel_Btn" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={formik.isSubmitting}>
                {isEdit ? "Save changes" : "Add Employee"}
              </Button>
            </div>
          </Form>
        </Container >
      </CommonModal>
    </Fragment>
  );
};

export default EmployeesTable;
